import { useRef, useCallback } from 'react';

/**
 * Custom hook for monitoring camera performance metrics
 * Tracks initialization time, capture time, and memory usage
 */
export const usePerformanceMonitor = () => {
  const metricsRef = useRef({
    initStartTime: null,
    initEndTime: null,
    captureStartTime: null,
    captureEndTime: null,
    memoryUsage: []
  });

  const startInitTimer = useCallback(() => {
    metricsRef.current.initStartTime = performance.now();
  }, []);

  const endInitTimer = useCallback(() => {
    metricsRef.current.initEndTime = performance.now();
    const initTime = metricsRef.current.initEndTime - metricsRef.current.initStartTime;
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`Camera initialization took: ${initTime.toFixed(2)}ms`);
    }
    
    return initTime;
  }, []);

  const startCaptureTimer = useCallback(() => {
    metricsRef.current.captureStartTime = performance.now();
  }, []);

  const endCaptureTimer = useCallback(() => {
    metricsRef.current.captureEndTime = performance.now();
    const captureTime = metricsRef.current.captureEndTime - metricsRef.current.captureStartTime;
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`Photo capture took: ${captureTime.toFixed(2)}ms`);
    }
    
    return captureTime;
  }, []);

  const recordMemoryUsage = useCallback(() => {
    if (performance.memory) {
      const memoryInfo = {
        timestamp: Date.now(),
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
      };
      
      metricsRef.current.memoryUsage.push(memoryInfo);
      
      // Keep only last 10 measurements
      if (metricsRef.current.memoryUsage.length > 10) {
        metricsRef.current.memoryUsage.shift();
      }
      
      if (process.env.NODE_ENV === 'development') {
        const usedMB = (memoryInfo.usedJSHeapSize / 1024 / 1024).toFixed(2);
        console.log(`Memory usage: ${usedMB}MB`);
      }
      
      return memoryInfo;
    }
    return null;
  }, []);

  const getMetrics = useCallback(() => {
    const initTime = metricsRef.current.initEndTime && metricsRef.current.initStartTime
      ? metricsRef.current.initEndTime - metricsRef.current.initStartTime
      : null;
      
    const captureTime = metricsRef.current.captureEndTime && metricsRef.current.captureStartTime
      ? metricsRef.current.captureEndTime - metricsRef.current.captureStartTime
      : null;

    return {
      initTime,
      captureTime,
      memoryUsage: [...metricsRef.current.memoryUsage]
    };
  }, []);

  const resetMetrics = useCallback(() => {
    metricsRef.current = {
      initStartTime: null,
      initEndTime: null,
      captureStartTime: null,
      captureEndTime: null,
      memoryUsage: []
    };
  }, []);

  return {
    startInitTimer,
    endInitTimer,
    startCaptureTimer,
    endCaptureTimer,
    recordMemoryUsage,
    getMetrics,
    resetMetrics
  };
};

export default usePerformanceMonitor;